import { useEffect, useRef } from "react";
import {
	type AmplitudeContextProperties,
	clearAmplitudeContextProperties,
	setAmplitudeContextProperties,
} from "#configuration/amplitude-context-properties";

/**
 * Hook para gerenciar propriedades contextuais do Amplitude automaticamente.
 * 
 * Adiciona as propriedades quando o componente monta e remove quando desmonta.
 * 
 * @param properties - Propriedades contextuais a serem adicionadas (deve incluir 'module')
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   useAmplitudeContextProperties({ module: "data-hub" });
 *   return <div>...</div>;
 * }
 * ```
 */
export function useAmplitudeContextProperties(
	properties: AmplitudeContextProperties,
): void {
	const keysRef = useRef<string[]>([]);

	useEffect(() => {
		const currentKeys = Object.keys(properties);

		// Limpa as chaves antigas antes de adicionar as novas
		if (keysRef.current.length > 0) {
			clearAmplitudeContextProperties(keysRef.current);
		}

		// Define as novas propriedades contextuais
		setAmplitudeContextProperties(properties);
		keysRef.current = currentKeys;

		// Limpa as propriedades quando o componente desmonta ou as propriedades mudam
		return () => {
			clearAmplitudeContextProperties(keysRef.current);
		};
	}, [properties]);
}
